import { Spin, Typography } from 'antd';
import { IconButton } from '@ydistri/ds';
import { COLORS } from '@ydistri/theme';
import React, { useMemo } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { css, styled, useTheme } from 'styled-components';
import { InfoIconTooltip } from '../../../components/icons/InfoIconTooltip';
import ScopeSharingIcon from '../../../components/domain/Scopes/ScopeSharingIcon';
import { CreateAsoButton } from '../components/CreateAsoButton';
import {
  dropdownAlign,
  OptionLeft,
  OptionRow,
  OptionTitle,
  StyledSelect,
} from '../components/selectStyledComponents';
import {
  ScopeControlRow,
  ScopeCreateButtonSlot,
  ScopeSelectedValue,
  ScopeSelectContainer,
  ScopeSharingButtonSlot,
} from './AsoScopeAssignmentStyledComponents';
import type { AsoScopeOption, AsoScopeSelectValue } from './asoScopeAssignmentTypes';

const ZERO_SKU_INFO = 'This Scope resolves to zero SKUs. Add rules before computing ASO.';

const ScopeOptionTitle = styled(OptionTitle)<{ $shared: boolean }>`
  ${({ $shared }) =>
    !$shared &&
    css`
      color: ${COLORS.GREY};
      font-style: italic;
    `}
`;

const ZeroSkuIcon = styled(AiOutlineInfoCircle)`
  color: ${COLORS.DANGER};
`;

interface AsoScopeSelectorProps {
  disabled: boolean;
  displayedScopeId?: number | null;
  hasPendingCreatedScope: boolean;
  hasSelectedScope: boolean;
  hasSelectedScopeSharingState: boolean;
  hasZeroResolvedSkus: boolean;
  isFetching: boolean;
  isSaving: boolean;
  isSelectedScopeShared: boolean;
  isSharingToggleDisabled: boolean;
  isUpdatingSharing: boolean;
  onChange: (value?: AsoScopeSelectValue) => void;
  onCreate: () => void;
  onToggleSharing: () => void;
  options: AsoScopeOption[];
}

const AsoScopeSelector: React.FC<AsoScopeSelectorProps> = ({
  disabled,
  displayedScopeId,
  hasPendingCreatedScope,
  hasSelectedScope,
  hasSelectedScopeSharingState,
  hasZeroResolvedSkus,
  isFetching,
  isSaving,
  isSelectedScopeShared,
  isSharingToggleDisabled,
  isUpdatingSharing,
  onChange,
  onCreate,
  onToggleSharing,
  options,
}) => {
  const theme = useTheme();
  const sharingActionLabel = isSelectedScopeShared ? 'Make local' : 'Make shared';
  const selectOptions = useMemo(
    () =>
      options.map(option => ({
        ...option,
        label: (
          <OptionRow>
            <OptionLeft>#{option.value}</OptionLeft>
            <ScopeOptionTitle
              $fullWidth
              $shared={option.shared}
              data-scope-sharing={option.shared ? 'shared' : 'private'}
              title={option.label}
            >
              {option.label}
            </ScopeOptionTitle>
          </OptionRow>
        ),
      })),
    [options],
  );

  return (
    <ScopeControlRow>
      <ScopeSelectContainer>
        <Spin spinning={isFetching}>
          <StyledSelect
            $compact
            $fullWidth
            allowClear
            aria-label="Reusable Scope"
            data-testid="aso-scope-select"
            disabled={disabled || isSaving}
            dropdownAlign={dropdownAlign}
            listHeight={window.innerHeight * 0.6}
            loading={isSaving}
            optionFilterProp="searchLabel"
            options={selectOptions}
            placeholder="Select a Scope"
            prefix={<Typography.Text strong>Scope:</Typography.Text>}
            showSearch
            size="middle"
            value={displayedScopeId ?? undefined}
            variant="outlined"
            labelRender={selectedOption => (
              <ScopeSelectedValue data-testid="aso-scope-selected-value">
                {selectedOption.label}
                {hasZeroResolvedSkus && (
                  <InfoIconTooltip
                    content={ZERO_SKU_INFO}
                    icon={<ZeroSkuIcon aria-label="Scope resolves to zero SKUs" />}
                  />
                )}
              </ScopeSelectedValue>
            )}
            onChange={onChange}
          />
        </Spin>
      </ScopeSelectContainer>
      {hasSelectedScope && hasSelectedScopeSharingState && (
        <ScopeSharingButtonSlot>
          <IconButton
            aria-disabled={disabled || isSaving || isSharingToggleDisabled}
            aria-label={sharingActionLabel}
            colorScheme={
              isSelectedScopeShared ? theme.iconColorSchemes.default : theme.iconColorSchemes.none
            }
            disabled={disabled || isSaving || isSharingToggleDisabled}
            icon={
              <ScopeSharingIcon
                color="currentColor"
                isShared={isSelectedScopeShared}
                label={isSelectedScopeShared ? 'Shared Scope' : 'Local Scope'}
                showTooltip={false}
              />
            }
            loading={isUpdatingSharing}
            size="small"
            tooltip={sharingActionLabel}
            onClick={onToggleSharing}
          />
        </ScopeSharingButtonSlot>
      )}
      <ScopeCreateButtonSlot>
        <CreateAsoButton
          disabled={disabled || isSaving}
          title={hasPendingCreatedScope ? 'Retry assignment' : 'Create scope'}
          onClick={onCreate}
        />
      </ScopeCreateButtonSlot>
    </ScopeControlRow>
  );
};

export default AsoScopeSelector;
